import { useState } from 'react';
import { Check, Palette, Settings2, RotateCcw, Sparkles } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import {
  useTheme,
  useCardStyle,
  THEME_CATEGORIES,
  THEME_CATEGORY_LABELS,
  THEME_METADATA,
  DEFAULT_GLASSMORPHISM,
  DEFAULT_BACKGROUND,
  BACKGROUND_PRESETS,
  PRESET_METADATA,
  type CardStyle,
  type GlassmorphismSettings,
  type BackgroundSettings,
  type BackgroundPreset,
} from '@/features/theme/contexts/ThemeContext';
import { cn } from '@/lib/utils';

interface ThemeSelectorProps {
  className?: string;
}

export function ThemeSelector({ className }: ThemeSelectorProps) {
  const {
    cardStyle,
    setCardStyle,
    glassmorphism,
    updateGlassmorphism,
    resetGlassmorphism,
    background,
    setBackground,
    updateBackground,
    resetBackground,
  } = useTheme();
  const { popoverStyleObject, borderClassName } = useCardStyle();
  const currentTheme = THEME_METADATA[cardStyle];
  const [activeTab, setActiveTab] = useState<'themes' | 'glass' | 'background'>('themes');

  // Check if glassmorphism settings are default
  const isGlassDefault =
    glassmorphism.blur === DEFAULT_GLASSMORPHISM.blur &&
    glassmorphism.opacity === DEFAULT_GLASSMORPHISM.opacity &&
    glassmorphism.saturation === DEFAULT_GLASSMORPHISM.saturation;

  // Check if background settings are default
  const isBackgroundDefault = background.preset === 'default';

  return (
    <Popover>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                className={cn(
                  'p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors',
                  className
                )}
                aria-label="Selecionar tema"
              >
                <Palette className="size-4" />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="text-xs">
              Tema: <span className="font-medium">{currentTheme.label}</span>
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent
        align="end"
        className={cn("w-[420px] p-0 border", borderClassName)}
        style={popoverStyleObject}
      >
        {/* Tabs */}
        <div className="flex border-b border-white/10">
          <button
            onClick={() => setActiveTab('themes')}
            className={cn(
              "flex-1 px-3 py-2.5 text-xs font-medium transition-colors flex items-center justify-center gap-1.5",
              activeTab === 'themes'
                ? "text-white bg-white/5 border-b-2 border-violet-500"
                : "text-zinc-400 hover:text-zinc-200 hover:bg-white/5"
            )}
          >
            <Palette className="size-3.5" />
            Temas
          </button>
          <button
            onClick={() => setActiveTab('glass')}
            className={cn(
              "flex-1 px-3 py-2.5 text-xs font-medium transition-colors flex items-center justify-center gap-1.5",
              activeTab === 'glass'
                ? "text-white bg-white/5 border-b-2 border-violet-500"
                : "text-zinc-400 hover:text-zinc-200 hover:bg-white/5"
            )}
          >
            <Settings2 className="size-3.5" />
            Glass
          </button>
          <button
            onClick={() => setActiveTab('background')}
            className={cn(
              "flex-1 px-3 py-2.5 text-xs font-medium transition-colors flex items-center justify-center gap-1.5",
              activeTab === 'background'
                ? "text-white bg-white/5 border-b-2 border-violet-500"
                : "text-zinc-400 hover:text-zinc-200 hover:bg-white/5"
            )}
          >
            <Sparkles className="size-3.5" />
            Background
          </button>
        </div>

        {/* Content */}
        <div className="p-3 max-h-[450px] overflow-y-auto">
          {activeTab === 'themes' && (
            <ThemesPanel
              cardStyle={cardStyle}
              setCardStyle={setCardStyle}
            />
          )}
          {activeTab === 'glass' && (
            <GlassmorphismPanel
              glassmorphism={glassmorphism}
              updateGlassmorphism={updateGlassmorphism}
              resetGlassmorphism={resetGlassmorphism}
              isDefault={isGlassDefault}
            />
          )}
          {activeTab === 'background' && (
            <BackgroundPanel
              background={background}
              setBackground={setBackground}
              updateBackground={updateBackground}
              resetBackground={resetBackground}
              isDefault={isBackgroundDefault}
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Themes Panel Component
interface ThemesPanelProps {
  cardStyle: CardStyle;
  setCardStyle: (style: CardStyle) => void;
}

function ThemesPanel({ cardStyle, setCardStyle }: ThemesPanelProps) {
  return (
    <div className="space-y-3">
      {Object.entries(THEME_CATEGORIES).map(([category, themes]) => (
        <div key={category}>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium mb-2 px-1">
            {THEME_CATEGORY_LABELS[category]}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {themes.map((theme: CardStyle) => {
              const meta = THEME_METADATA[theme];
              const isSelected = cardStyle === theme;

              return (
                <button
                  key={theme}
                  onClick={() => setCardStyle(theme)}
                  className={cn(
                    'flex items-center gap-2 p-2 rounded-lg transition-all text-left',
                    isSelected
                      ? 'bg-violet-500/20 ring-1 ring-violet-500/50'
                      : 'bg-white/5 hover:bg-white/10'
                  )}
                >
                  {/* Color preview */}
                  <div
                    className="w-5 h-5 rounded-full border border-white/20 flex-shrink-0"
                    style={{ backgroundColor: meta.previewColor }}
                  />

                  {/* Theme info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate text-white">{meta.label}</p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {meta.description}
                    </p>
                  </div>

                  {/* Check icon */}
                  {isSelected && (
                    <Check
                      className="size-3.5 flex-shrink-0"
                      style={{ color: meta.accentColor }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// Glassmorphism Panel Component
interface GlassmorphismPanelProps {
  glassmorphism: GlassmorphismSettings;
  updateGlassmorphism: (key: keyof GlassmorphismSettings, value: number) => void;
  resetGlassmorphism: () => void;
  isDefault: boolean;
}

function GlassmorphismPanel({
  glassmorphism,
  updateGlassmorphism,
  resetGlassmorphism,
  isDefault
}: GlassmorphismPanelProps) {
  return (
    <div className="space-y-6">
      {/* Header with reset button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-white">Configurações de Glassmorphism</h3>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Ajuste o efeito de vidro em tempo real
          </p>
        </div>
        <button
          onClick={resetGlassmorphism}
          disabled={isDefault}
          className={cn(
            "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all",
            isDefault
              ? "text-zinc-600 cursor-not-allowed"
              : "text-zinc-400 hover:text-white hover:bg-white/10"
          )}
        >
          <RotateCcw className="size-3" />
          Reset
        </button>
      </div>

      {/* Preview Card */}
      <div
        className="rounded-lg p-4 border border-white/10"
        style={{
          background: `rgba(139, 92, 246, ${glassmorphism.opacity / 100 / 3})`,
          backdropFilter: `blur(${glassmorphism.blur}px) saturate(${glassmorphism.saturation}%)`,
          WebkitBackdropFilter: `blur(${glassmorphism.blur}px) saturate(${glassmorphism.saturation}%)`,
        }}
      >
        <p className="text-xs text-zinc-400 mb-1">Preview</p>
        <p className="text-sm font-medium text-white">Card com Glassmorphism</p>
        <p className="text-xs text-zinc-400 mt-1">
          blur: {glassmorphism.blur}px | opacity: {glassmorphism.opacity}% | sat: {glassmorphism.saturation}%
        </p>
      </div>

      {/* Sliders */}
      <div className="space-y-5">
        {/* Blur Slider */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-zinc-300">
              Blur (Desfoque)
            </label>
            <span className="text-xs text-violet-400 font-mono">
              {glassmorphism.blur}px
            </span>
          </div>
          <Slider
            value={[glassmorphism.blur]}
            onValueChange={([value]) => updateGlassmorphism('blur', value)}
            min={0}
            max={30}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-[10px] text-zinc-600">
            <span>0px</span>
            <span>15px</span>
            <span>30px</span>
          </div>
        </div>

        {/* Opacity Slider */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-zinc-300">
              Opacidade (Transparência)
            </label>
            <span className="text-xs text-violet-400 font-mono">
              {glassmorphism.opacity}%
            </span>
          </div>
          <Slider
            value={[glassmorphism.opacity]}
            onValueChange={([value]) => updateGlassmorphism('opacity', value)}
            min={10}
            max={90}
            step={5}
            className="w-full"
          />
          <div className="flex justify-between text-[10px] text-zinc-600">
            <span>10% (mais transparente)</span>
            <span>90% (mais sólido)</span>
          </div>
        </div>

        {/* Saturation Slider */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-zinc-300">
              Saturação (Vivacidade)
            </label>
            <span className="text-xs text-violet-400 font-mono">
              {glassmorphism.saturation}%
            </span>
          </div>
          <Slider
            value={[glassmorphism.saturation]}
            onValueChange={([value]) => updateGlassmorphism('saturation', value)}
            min={100}
            max={300}
            step={10}
            className="w-full"
          />
          <div className="flex justify-between text-[10px] text-zinc-600">
            <span>100% (normal)</span>
            <span>200%</span>
            <span>300% (vibrante)</span>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-3 rounded-lg bg-white/5 border border-white/5">
        <p className="text-[10px] text-zinc-500 leading-relaxed">
          <span className="text-zinc-400 font-medium">Dica:</span> Valores padrão são
          blur 15px, opacidade 35% e saturação 180%. Alterações são salvas automaticamente
          e aplicadas em tempo real em todos os cards e painéis.
        </p>
      </div>
    </div>
  );
}

// Background Panel Component
interface BackgroundPanelProps {
  background: BackgroundSettings;
  setBackground: (settings: BackgroundSettings) => void;
  updateBackground: <K extends keyof BackgroundSettings>(key: K, value: BackgroundSettings[K]) => void;
  resetBackground: () => void;
  isDefault: boolean;
}

// Color presets for quick selection
const COLOR_PRESETS = [
  { name: 'Roxo', color1: '#581c87', color2: '#4338ca', particle: '#8b5cf6' },
  { name: 'Azul', color1: '#1e3a8a', color2: '#0369a1', particle: '#38bdf8' },
  { name: 'Ciano', color1: '#164e63', color2: '#0e7490', particle: '#22d3ee' },
  { name: 'Verde', color1: '#14532d', color2: '#166534', particle: '#4ade80' },
  { name: 'Âmbar', color1: '#78350f', color2: '#92400e', particle: '#fbbf24' },
  { name: 'Rosa', color1: '#831843', color2: '#be185d', particle: '#f472b6' },
  { name: 'Branco', color1: '#374151', color2: '#4b5563', particle: '#ffffff' },
];

// Base color presets
const BASE_COLOR_PRESETS = [
  { name: 'Preto', color: '#09090b' },
  { name: 'Pure Black', color: '#000000' },
  { name: 'Cinza', color: '#18181b' },
  { name: 'Azul Escuro', color: '#020617' },
  { name: 'Índigo', color: '#0a0a1a' },
];

function BackgroundPanel({
  background,
  setBackground,
  updateBackground,
  resetBackground,
  isDefault
}: BackgroundPanelProps) {
  // Apply preset
  const applyPreset = (presetName: Exclude<BackgroundPreset, 'custom'>) => {
    const preset = BACKGROUND_PRESETS[presetName];
    setBackground({ ...preset, preset: presetName });
  };

  // Mark as custom when user changes settings
  const updateWithCustom = <K extends keyof BackgroundSettings>(key: K, value: BackgroundSettings[K]) => {
    if (background.preset !== 'custom') {
      updateBackground('preset', 'custom');
    }
    updateBackground(key, value);
  };

  return (
    <div className="space-y-5">
      {/* Header with reset button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-white">Configurações de Background</h3>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Personalize as nebulosas e partículas
          </p>
        </div>
        <button
          onClick={resetBackground}
          disabled={isDefault}
          className={cn(
            "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all",
            isDefault
              ? "text-zinc-600 cursor-not-allowed"
              : "text-zinc-400 hover:text-white hover:bg-white/10"
          )}
        >
          <RotateCcw className="size-3" />
          Reset
        </button>
      </div>

      {/* Presets */}
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium px-1">
          Presets
        </p>
        <div className="grid grid-cols-5 gap-2">
          {(Object.keys(PRESET_METADATA) as Exclude<BackgroundPreset, 'custom'>[]).map((presetKey) => {
            const meta = PRESET_METADATA[presetKey];
            const presetConfig = BACKGROUND_PRESETS[presetKey];
            const isSelected = background.preset === presetKey;

            return (
              <button
                key={presetKey}
                onClick={() => applyPreset(presetKey)}
                className={cn(
                  "relative flex flex-col items-center gap-1.5 p-2 rounded-lg transition-all",
                  isSelected
                    ? "ring-2 ring-violet-500 bg-white/10"
                    : "bg-white/5 hover:bg-white/10"
                )}
              >
                <div className="flex -space-x-1.5">
                  <div
                    className="w-3 h-3 rounded-full border border-white/20"
                    style={{ backgroundColor: presetConfig.nebulaColor1 }}
                  />
                  <div
                    className="w-3 h-3 rounded-full border border-white/20"
                    style={{ backgroundColor: presetConfig.nebulaColor2 }}
                  />
                  <div
                    className="w-3 h-3 rounded-full border border-white/20"
                    style={{ backgroundColor: presetConfig.particleColor }}
                  />
                </div>
                <span className="text-[9px] text-zinc-400 text-center leading-tight">{meta.label}</span>
                {isSelected && (
                  <Check className="absolute top-1 right-1 size-2.5 text-violet-400" />
                )}
              </button>
            );
          })}
        </div>
        {background.preset === 'custom' && (
          <p className="text-[9px] text-amber-400/80 px-1">Configurações personalizadas</p>
        )}
      </div>

      {/* Toggles */}
      <div className="space-y-2">
        <div className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/5">
          <div>
            <p className="text-xs font-medium text-zinc-300">Nebulosas</p>
            <p className="text-[10px] text-zinc-500">Efeito de nuvens coloridas</p>
          </div>
          <Switch
            checked={background.enableNebulas}
            onCheckedChange={(checked) => updateWithCustom('enableNebulas', checked)}
          />
        </div>
        <div className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/5">
          <div>
            <p className="text-xs font-medium text-zinc-300">Partículas</p>
            <p className="text-[10px] text-zinc-500">Rede de pontos animados</p>
          </div>
          <Switch
            checked={background.enableParticles}
            onCheckedChange={(checked) => updateWithCustom('enableParticles', checked)}
          />
        </div>
        <div className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/5">
          <div>
            <p className="text-xs font-medium text-zinc-300">Pausar Animações</p>
            <p className="text-[10px] text-zinc-500">Economiza CPU/bateria</p>
          </div>
          <Switch
            checked={background.pauseAnimations}
            onCheckedChange={(checked) => updateWithCustom('pauseAnimations', checked)}
          />
        </div>
      </div>

      {/* Base Color */}
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium px-1">
          Cor de Fundo
        </p>
        <div className="flex gap-2">
          {BASE_COLOR_PRESETS.map((preset) => {
            const isSelected = background.baseColor === preset.color;
            return (
              <button
                key={preset.name}
                onClick={() => updateWithCustom('baseColor', preset.color)}
                className={cn(
                  "flex flex-col items-center gap-1 p-1.5 rounded-lg transition-all flex-1",
                  isSelected
                    ? "ring-2 ring-violet-500 bg-white/10"
                    : "bg-white/5 hover:bg-white/10"
                )}
              >
                <div
                  className="w-full h-4 rounded border border-white/20"
                  style={{ backgroundColor: preset.color }}
                />
                <span className="text-[8px] text-zinc-400">{preset.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Color Presets */}
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium px-1">
          Paleta de Cores
        </p>
        <div className="grid grid-cols-7 gap-1.5">
          {COLOR_PRESETS.map((preset) => {
            const isSelected =
              background.nebulaColor1 === preset.color1 &&
              background.nebulaColor2 === preset.color2 &&
              background.particleColor === preset.particle;

            return (
              <button
                key={preset.name}
                onClick={() => {
                  updateWithCustom('nebulaColor1', preset.color1);
                  updateBackground('nebulaColor2', preset.color2);
                  updateBackground('particleColor', preset.particle);
                }}
                className={cn(
                  "relative flex flex-col items-center gap-1 p-1.5 rounded-lg transition-all",
                  isSelected
                    ? "ring-2 ring-violet-500 bg-white/10"
                    : "bg-white/5 hover:bg-white/10"
                )}
              >
                <div className="flex -space-x-1">
                  <div
                    className="w-3.5 h-3.5 rounded-full border border-white/20"
                    style={{ backgroundColor: preset.color1 }}
                  />
                  <div
                    className="w-3.5 h-3.5 rounded-full border border-white/20"
                    style={{ backgroundColor: preset.color2 }}
                  />
                </div>
                <span className="text-[8px] text-zinc-400">{preset.name}</span>
                {isSelected && (
                  <Check className="absolute top-0.5 right-0.5 size-2 text-violet-400" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Nebula Settings */}
      {background.enableNebulas && (
        <div className="space-y-3 p-3 rounded-lg bg-white/5 border border-white/5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium">
            Nebulosas
          </p>

          {/* Nebula Intensity */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-zinc-300">Intensidade</label>
              <span className="text-xs text-violet-400 font-mono">{background.nebulaIntensity}%</span>
            </div>
            <Slider
              value={[background.nebulaIntensity]}
              onValueChange={([value]) => updateWithCustom('nebulaIntensity', value)}
              min={0}
              max={150}
              step={5}
              className="w-full"
            />
          </div>

          {/* Nebula Blur */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-zinc-300">Blur (Desfoque)</label>
              <span className="text-xs text-violet-400 font-mono">{background.nebulaBlur}px</span>
            </div>
            <Slider
              value={[background.nebulaBlur]}
              onValueChange={([value]) => updateWithCustom('nebulaBlur', value)}
              min={50}
              max={150}
              step={5}
              className="w-full"
            />
          </div>

          {/* Nebula Size */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-zinc-300">Tamanho</label>
              <span className="text-xs text-violet-400 font-mono">{background.nebulaSize}%</span>
            </div>
            <Slider
              value={[background.nebulaSize]}
              onValueChange={([value]) => updateWithCustom('nebulaSize', value)}
              min={50}
              max={150}
              step={5}
              className="w-full"
            />
          </div>

          {/* Animation Speed */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-zinc-300">Velocidade Animação</label>
              <span className="text-xs text-violet-400 font-mono">{background.animationSpeed.toFixed(1)}x</span>
            </div>
            <Slider
              value={[background.animationSpeed * 10]}
              onValueChange={([value]) => updateWithCustom('animationSpeed', value / 10)}
              min={3}
              max={20}
              step={1}
              className="w-full"
            />
          </div>

          {/* Noise Opacity */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-zinc-300">Textura Ruído</label>
              <span className="text-xs text-violet-400 font-mono">{background.noiseOpacity.toFixed(1)}%</span>
            </div>
            <Slider
              value={[background.noiseOpacity * 10]}
              onValueChange={([value]) => updateWithCustom('noiseOpacity', value / 10)}
              min={0}
              max={50}
              step={1}
              className="w-full"
            />
          </div>
        </div>
      )}

      {/* Particle Settings */}
      {background.enableParticles && (
        <div className="space-y-3 p-3 rounded-lg bg-white/5 border border-white/5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium">
            Partículas
          </p>

          {/* Particle Count */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-zinc-300">Quantidade</label>
              <span className="text-xs text-violet-400 font-mono">{background.particleCount}</span>
            </div>
            <Slider
              value={[background.particleCount]}
              onValueChange={([value]) => updateWithCustom('particleCount', value)}
              min={30}
              max={300}
              step={10}
              className="w-full"
            />
          </div>

          {/* Particle Speed */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-zinc-300">Velocidade</label>
              <span className="text-xs text-violet-400 font-mono">{background.particleSpeed.toFixed(1)}</span>
            </div>
            <Slider
              value={[background.particleSpeed * 10]}
              onValueChange={([value]) => updateWithCustom('particleSpeed', value / 10)}
              min={1}
              max={10}
              step={1}
              className="w-full"
            />
          </div>

          {/* Particle Size */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-zinc-300">Tamanho</label>
              <span className="text-xs text-violet-400 font-mono">{background.particleSize.toFixed(1)}px</span>
            </div>
            <Slider
              value={[background.particleSize * 10]}
              onValueChange={([value]) => updateWithCustom('particleSize', value / 10)}
              min={5}
              max={30}
              step={1}
              className="w-full"
            />
          </div>

          {/* Particle Opacity */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-zinc-300">Opacidade Partículas</label>
              <span className="text-xs text-violet-400 font-mono">{background.particleOpacity}%</span>
            </div>
            <Slider
              value={[background.particleOpacity]}
              onValueChange={([value]) => updateWithCustom('particleOpacity', value)}
              min={5}
              max={50}
              step={5}
              className="w-full"
            />
          </div>

          {/* Line Opacity */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-zinc-300">Opacidade Linhas</label>
              <span className="text-xs text-violet-400 font-mono">{background.lineOpacity}%</span>
            </div>
            <Slider
              value={[background.lineOpacity]}
              onValueChange={([value]) => updateWithCustom('lineOpacity', value)}
              min={0}
              max={30}
              step={1}
              className="w-full"
            />
          </div>

          {/* Line Range */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-zinc-300">Range Linhas</label>
              <span className="text-xs text-violet-400 font-mono">{background.lineRange}px</span>
            </div>
            <Slider
              value={[background.lineRange]}
              onValueChange={([value]) => updateWithCustom('lineRange', value)}
              min={50}
              max={200}
              step={10}
              className="w-full"
            />
          </div>
        </div>
      )}

      {/* Info */}
      <div className="p-2.5 rounded-lg bg-white/5 border border-white/5">
        <p className="text-[10px] text-zinc-500 leading-relaxed">
          <span className="text-zinc-400 font-medium">Dica:</span> Use presets para aplicar configurações
          completas. Alterações manuais marcam como "personalizado". Pause animações para economizar recursos.
        </p>
      </div>
    </div>
  );
}
